/**
 * Status and priority color mapping for chips/badges (MUI color names or hex).
 */
export const statusColors = {
    new: 'info',
    quoted: 'warning',
    accepted: 'primary',
    in_progress: 'info',
    inprogress: 'info', // ServiceOrder backend stores this camelCase ("InProgress"), which normalizes to this key (no separator to convert)
    completed: 'success',
    cancelled: 'error',
    closed: 'error',
};

export const priorityColors = {
    low: 'success',
    medium: 'info',
    high: 'warning',
    urgent: 'error',
};

/** Get MUI chip color for a status name (case-insensitive). */
export const getStatusColor = (status) => {
    if (!status) return 'default';
    const key = String(status).toLowerCase().replace(/\s+/g, '_');
    return statusColors[key] || 'default';
};

/** Get MUI chip color for a priority name (case-insensitive). */
export const getPriorityColor = (priority) => {
    if (!priority) return 'default';
    const key = String(priority).toLowerCase().replace(/\s+/g, '_');
    return priorityColors[key] || 'default';
};

/** Format status/priority label for display (e.g. "in_progress" -> "In Progress"). */
export const formatStatusLabel = (value) => {
    if (value == null || value === '') return '';
    const str = String(value).trim();
    return str
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
};

/**
 * Format status label with no space between words (e.g. "in_progress" -> "InProgress"),
 * matching how ServiceOrderStatus values are already stored/displayed elsewhere in the app.
 */
export const formatStatusLabelCompact = (value) => {
    if (value == null || value === '') return '';
    return String(value)
        .trim()
        .split(/[_\s]+/)
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join('');
};

/** Get MUI chip color for service order status. */
export const getServiceOrderStatusChipColor = (status) => getStatusColor(status);

/** Get MUI chip color for service order priority. */
export const getServiceOrderPriorityChipColor = (priority) => getPriorityColor(priority);

/** Get MUI chip color for report status. */
export const getReportStatusColor = (status) => getStatusColor(status);

/** Get MUI chip color for invoice status. */
export const getInvoiceStatusChipColor = (status) => {
    if (!status) return 'default';
    const statusMap = {
        DRAFT: 'default',
        APPROVED: 'success',
        SENT: 'info',
        ACTION: 'warning',
        PARTIAL: 'warning',
        PAID: 'success',
        OVERDUE: 'error',
        VOID: 'error',
    };
    return statusMap[status] || 'default';
};

/** Get MUI chip color for a test attempt result (pass / pending / fail). */
export const getTestResultColor = (result) => (
    result === 'pass' ? 'success' : result === 'pending' ? 'warning' : 'error'
);

const certificationStatusColors = {
    active: 'success',
    expiring_soon: 'warning',
    expired: 'error',
    revoked: 'error',
    superseded: 'default',
    // Synthetic statuses from the compliance matrix (not a real CertificationRecord.status) —
    // "eligible" = workflow complete, certificate not yet issued; "not_certified" = workflow incomplete.
    eligible: 'info',
    not_certified: 'default',
};

/** Get MUI chip color for a CertificationRecord status. */
export const getCertificationStatusColor = (status) => certificationStatusColors[status] || 'default';

const decisionStatusColors = {
    pending: 'warning',
    approved: 'success',
    declined: 'error',
};

/** Get MUI chip color for a LeaveRequest/Expense status (pending/approved/declined). */
export const getDecisionStatusColor = (status) => decisionStatusColors[status] || 'default';

const attendanceStatusColors = {
    enrolled: 'info',
    attended: 'success',
    absent: 'error',
    withdrawn: 'default',
};

/** Get MUI chip color for an Enrolment attendance_status. */
export const getAttendanceStatusColor = (status) => attendanceStatusColors[status] || 'default';

const commercialStatusColors = {
    // Enquiry
    new: 'info',
    quoted: 'primary',
    converted_to_so: 'success',
    lost: 'default',
    cancelled: 'default',
    // Quotation
    draft: 'default',
    sent: 'primary',
    accepted: 'success',
    rejected: 'error',
    expired: 'warning',
    superseded: 'default',
    // Purchase order
    received: 'info',
    active: 'primary',
    completed: 'success',
    // Enquiry form link
    open: 'info',
    submitted: 'success',
    revoked: 'default',
};

/** Get MUI chip color for an Enquiry / Quotation / Purchase Order status (Sales module). */
export const getCommercialStatusColor = (status) => commercialStatusColors[status] || 'default';

export default statusColors;
